import { z } from 'zod'
import {
  healthSchema,
  provenanceSchema,
  sourceKindSchema,
  unitSchema,
} from './primitives.js'

/** Definición de un KPI. Es un registro en base: agregar KPI no es un deploy. */
export const metricDefinitionSchema = z.object({
  slug: z.string(),
  nombre: z.string(),
  definicion: z.string(),
  /** Fórmula legible, se muestra en la tarjeta. null si es dato primario. */
  formula: z.string().nullable(),
  unidad: unitSchema,
  /** Benchmark legible, ej. ">110% = excelente". */
  benchmark: z.string().nullable(),
  direccion: z.enum(['higher_better', 'lower_better']),
  umbralOk: z.number().nullable(),
  umbralWarning: z.number().nullable(),
  source: sourceKindSchema,
  ownerId: z.string().nullable(),
})
export type MetricDefinition = z.infer<typeof metricDefinitionSchema>

/** Valor de un KPI en un período, ya resuelto con semáforo y proveniencia. */
export const metricValueSchema = z.object({
  slug: z.string(),
  nombre: z.string(),
  unidad: unitSchema,
  /** 'YYYY-MM' */
  periodo: z.string(),
  /** null = sin dato. La UI muestra el estado vacío que invita a cargar. */
  valor: z.number().nullable(),
  health: healthSchema,
  /** Variación vs período anterior, en %. null si no hay comparable. */
  deltaPct: z.number().nullable(),
  /** Últimos 12 períodos para el sparkline, del más viejo al más nuevo. */
  sparkline: z.array(z.number().nullable()),
  benchmark: z.string().nullable(),
  formula: z.string().nullable(),
  provenance: provenanceSchema,
})
export type MetricValue = z.infer<typeof metricValueSchema>

export const createDatapointSchema = z.object({
  periodo: z.string().regex(/^\d{4}-\d{2}$/, 'formato esperado YYYY-MM'),
  valor: z.number(),
  nota: z.string().max(500).optional(),
})
export type CreateDatapointInput = z.infer<typeof createDatapointSchema>

/**
 * Base de un delta: el período contra el que se compara y su valor.
 *
 * Regla de producto: prohibido un porcentaje cuya base no esté en la pantalla.
 * Todo delta que se renderice tiene que poder mostrar esto al lado.
 */
export const deltaBaseSchema = z.object({
  /** 'YYYY-MM' del período de comparación. */
  periodo: z.string(),
  /** Valor del período de comparación. null si no hay dato para comparar. */
  valor: z.number().nullable(),
})
export type DeltaBase = z.infer<typeof deltaBaseSchema>

/**
 * Devuelve la base de un delta a partir del sparkline de un MetricValue.
 * El sparkline va del más viejo al más nuevo y el último punto es el período
 * actual, así que la base es el penúltimo.
 */
export function deltaBaseOf(
  metric: Pick<MetricValue, 'periodo' | 'sparkline'>,
  periodoBase: string,
): DeltaBase {
  const n = metric.sparkline.length
  const anterior = n >= 2 ? metric.sparkline[n - 2] : undefined
  return { periodo: periodoBase, valor: anterior ?? null }
}
